export const MAP_OVERLAY_CLUSTER_RADIUS = 50;
export const MAP_OVERLAY_CLUSTER_MAX_ZOOM = 16;
export const MAP_OVERLAY_POPUP_PAGE_SIZE = 20;
export const MAP_OVERLAY_LIST_PAGE_SIZE = 25;

export const VOTER_SOURCE_ID = 'canvassing-session-voters';
export const VISIT_SOURCE_ID = 'canvassing-session-door-visits';
export const VOTER_CLUSTER_LAYER_ID = 'canvassing-session-voter-clusters';
export const VOTER_POINT_LAYER_ID = 'canvassing-session-voter-points';
export const VOTER_COUNT_LAYER_ID = 'canvassing-session-voter-cluster-count';
export const VISIT_CLUSTER_LAYER_ID = 'canvassing-session-visit-clusters';
export const VISIT_POINT_LAYER_ID = 'canvassing-session-visit-points';
export const VISIT_COUNT_LAYER_ID = 'canvassing-session-visit-cluster-count';
export const VISIT_ICON_ID = 'canvassing-session-door-visit-square';

export const MAP_OVERLAY_COPY = {
  votersLabel: 'Voters',
  visitsLabel: 'Door visits',
  votersUnavailable: 'Nearby voters unavailable: no valid session GPS locations.',
  noLocations: 'No mapped locations are available for this session.',
  noVoters: 'No voters found in the session area.',
  noVisits: 'No door visits with valid locations.',
  loadingMore: 'Loading more…',
  incompleteVoters: 'Voter results are incomplete.',
  incompleteVisits: 'Door visit results are incomplete.',
  stale: 'Displayed map records may be stale.',
  retryVoters: 'Retry voters',
  retryVisits: 'Retry door visits',
  addressUnavailable: 'Address unavailable',
  viewVoter: 'View voter',
  unmatchedVisit: 'Unmatched door visit',
  viewDoorVisit: 'View door visit',
  showMapRecords: 'Show map records',
  hideMapRecords: 'Hide map records',
  closePopup: 'Close map records',
  popupLabel: 'Map records at this location',
  listLabel: 'Loaded map records',
  voterLegend: 'Voters are shown as green circles.',
  visitLegend: 'Door visits are shown as orange squares.',
  clusterLegend: 'Grouped markers show a count. Use Show map records to review each loaded record.',
  loadingVoters: 'Loading voters…',
  loadingVisits: 'Loading door visits…',
  refreshing: 'Refreshing map records…',
  voterRequestError: 'Could not load nearby voters. Please try again.',
  visitRequestError: 'Could not load door visits for the map. Please try again.',
  malformed: 'The map response was incomplete. Please try again.',
  previousRecords: 'Previous map records',
  nextRecords: 'Next map records',
} as const;

export class MapOverlayPageError extends Error {
  constructor() {
    super(MAP_OVERLAY_COPY.malformed);
    this.name = 'MapOverlayPageError';
  }
}

export type MapOverlayBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

export type MapOverlayVoter = {
  id: string;
  householdId: string;
  displayName: string | null;
  address: string | null;
  lat: number;
  lng: number;
};

export type MapOverlayVisit = {
  doorVisitId: number;
  lat: number;
  lng: number;
  dwellStartedAt: string;
  dwellEndedAt: string;
  status: string;
  matchedVoterRecordId: string | null;
  voterName: string | null;
  predictedAddress: string | null;
};

export type MapOverlayPage<T> = {
  items: T[];
  hasNextPage: boolean;
  endCursor: string | null;
};

export type OverlayFeature = {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: { overlay: 'voter' | 'visit'; recordId: string };
};

export type OverlayFeatureCollection = {
  type: 'FeatureCollection';
  features: OverlayFeature[];
};

export type MapPopupRecord =
  | { kind: 'voter'; voter: MapOverlayVoter }
  | { kind: 'visit'; visit: MapOverlayVisit };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isDecimalId = (value: unknown): value is string => typeof value === 'string' && /^[0-9]+$/.test(value);

const isCoordinate = (value: unknown, minimum: number, maximum: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum;

const optionalText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const readPageInfo = (value: unknown): { hasNextPage: boolean; endCursor: string | null } => {
  const pageInfo = isRecord(value) ? value : null;
  const hasNextPage = pageInfo?.hasNextPage;
  const endCursor = pageInfo?.endCursor;
  if (typeof hasNextPage !== 'boolean') throw new MapOverlayPageError();
  if (hasNextPage) {
    if (typeof endCursor !== 'string' || endCursor.trim().length === 0) throw new MapOverlayPageError();
    return { hasNextPage, endCursor };
  }
  if (endCursor !== null && endCursor !== undefined) throw new MapOverlayPageError();
  return { hasNextPage: false, endCursor: null };
};

export const normalizeVotersInBoundsPage = (value: unknown): MapOverlayPage<MapOverlayVoter> => {
  const result = isRecord(value) ? value : null;
  if (result === null || !Array.isArray(result.items) || !isRecord(result.pageInfo)) throw new MapOverlayPageError();
  const pageInfo = readPageInfo(result.pageInfo);
  const items = result.items.flatMap(item => {
    const record = isRecord(item) ? item : null;
    if (record === null || !isDecimalId(record.id) || !isDecimalId(record.householdId)) return [];
    if (!isCoordinate(record.lat, -90, 90) || !isCoordinate(record.lng, -180, 180)) return [];
    return [{
      id: record.id,
      householdId: record.householdId,
      displayName: optionalText(record.displayName),
      address: optionalText(record.address),
      lat: record.lat,
      lng: record.lng,
    }];
  });
  return { items, ...pageInfo };
};

export const normalizeDoorVisitMapPage = (value: unknown): MapOverlayPage<MapOverlayVisit> => {
  if (value === null) return { items: [], hasNextPage: false, endCursor: null };
  const result = isRecord(value) ? value : null;
  if (result === null || !Array.isArray(result.items) || !isRecord(result.pageInfo)) throw new MapOverlayPageError();
  const pageInfo = readPageInfo(result.pageInfo);
  const items = result.items.flatMap(item => {
    const record = isRecord(item) ? item : null;
    const doorVisitId = record?.doorVisitId;
    if (
      record === null
      || typeof doorVisitId !== 'number'
      || !Number.isSafeInteger(doorVisitId)
      || doorVisitId <= 0
      || !isCoordinate(record.lat, -90, 90)
      || !isCoordinate(record.lng, -180, 180)
    ) {
      return [];
    }
    const matched = record.matchedVoterRecordId;
    return [{
      doorVisitId,
      lat: record.lat,
      lng: record.lng,
      dwellStartedAt: typeof record.dwellStartedAt === 'string' ? record.dwellStartedAt : '',
      dwellEndedAt: typeof record.dwellEndedAt === 'string' ? record.dwellEndedAt : '',
      status: typeof record.status === 'string' ? record.status : '',
      matchedVoterRecordId: isDecimalId(matched) ? matched : null,
      voterName: optionalText(record.voterName),
      predictedAddress: optionalText(record.predictedAddress),
    }];
  });
  return { items, ...pageInfo };
};

export const sameMapOverlayBounds = (left: MapOverlayBounds | null, right: MapOverlayBounds | null): boolean => {
  if (left === null || right === null) return left === right;
  return left.north === right.north && left.south === right.south && left.east === right.east && left.west === right.west;
};

export const suppressedVoterIds = (visits: readonly MapOverlayVisit[], renderVisits: boolean): ReadonlySet<string> => {
  if (!renderVisits) return new Set();
  const ids = new Set<string>();
  for (const visit of visits) {
    if (visit.matchedVoterRecordId !== null) ids.add(visit.matchedVoterRecordId);
  }
  return ids;
};

export const visibleOverlayVoters = (
  voters: readonly MapOverlayVoter[],
  visits: readonly MapOverlayVisit[],
  renderVoters: boolean,
  renderVisits: boolean,
): MapOverlayVoter[] => {
  if (!renderVoters) return [];
  const suppressed = suppressedVoterIds(visits, renderVisits);
  return voters.filter(voter => !suppressed.has(voter.id));
};

export const coordinateKey = (lat: number, lng: number): string => `${lat},${lng}`;

export const recordsAtCoordinates = (
  coordinates: readonly (readonly [number, number])[],
  voters: readonly MapOverlayVoter[],
  visits: readonly MapOverlayVisit[],
): MapPopupRecord[] => {
  const keys = new Set(coordinates.map(([lng, lat]) => coordinateKey(lat, lng)));
  const matchedVoters = voters.filter(voter => keys.has(coordinateKey(voter.lat, voter.lng)));
  const matchedVisits = visits.filter(visit => keys.has(coordinateKey(visit.lat, visit.lng)));
  return [
    ...matchedVoters.map(voter => ({ kind: 'voter' as const, voter })),
    ...matchedVisits.map(visit => ({ kind: 'visit' as const, visit })),
  ];
};

export const clusterClickAction = (currentZoom: number, expansionZoom: number): 'zoom' | 'list' =>
  expansionZoom > currentZoom ? 'zoom' : 'list';

export const toOverlayFeatureCollection = (
  records: readonly { id: string; lat: number; lng: number }[],
  overlay: 'voter' | 'visit',
): OverlayFeatureCollection => ({
  type: 'FeatureCollection',
  features: records.map(record => ({
    type: 'Feature',
    id: `${overlay}:${record.id}`,
    geometry: { type: 'Point', coordinates: [record.lng, record.lat] },
    properties: { overlay, recordId: record.id },
  })),
});

export type FitBoundsTarget = {
  west: number;
  south: number;
  east: number;
  north: number;
};

export const unionFitBounds = (
  serverBounds: MapOverlayBounds | null,
  points: readonly { lat: number; lng: number }[],
): FitBoundsTarget | null => {
  const seeds: FitBoundsTarget[] = [];
  if (serverBounds !== null) {
    seeds.push({
      west: serverBounds.west,
      south: serverBounds.south,
      east: serverBounds.east,
      north: serverBounds.north,
    });
  }
  for (const point of points) seeds.push({ west: point.lng, south: point.lat, east: point.lng, north: point.lat });
  const first = seeds[0];
  if (first === undefined) return null;
  return seeds.reduce((bounds, point) => ({
    west: Math.min(bounds.west, point.west),
    south: Math.min(bounds.south, point.south),
    east: Math.max(bounds.east, point.east),
    north: Math.max(bounds.north, point.north),
  }));
};

export const isDegenerateFitBounds = (bounds: FitBoundsTarget): boolean =>
  bounds.west === bounds.east && bounds.south === bounds.north;
